import PropTypes from "prop-types";
import React, { useMemo } from "react";

import { classNames } from "@swa-ui/string";
import { getUniqueId } from "@swa-ui/string";

import { Layout } from "../Layout";
import { OptionListItem, optionListItemPropTypes } from "../OptionListItem";
import { Separator, separatorPropTypes } from "../Separator";
import styles from "./OptionList.module.scss";

/**
 * OptionList renders a list of items comprised of a title, a fixed arrow suffix icon,
 * an optional prefix icon, optional children, and optional separators between each item.
 * Each item is provided through the `options` prop, and is the props provided to individually
 * rendered `OptionListItem` components.
 */
export const OptionList = (props) => {
  const { className, options, prefixIcon, separators, suffixIcon } = props;
  const identifiers = useMemo(() => options.map(() => getUniqueId("option-list-item")), [options]);

  return <div className={className}>{options.map(renderOption)}</div>;

  function renderOption(option, index) {
    const optionProps = {
      prefixIcon,
      suffixIcon,
      ...option,
    };

    return (
      <div key={identifiers[index]}>
        <Layout bottom="xlarge">
          <OptionListItem {...optionProps} />
        </Layout>
        {renderSeparator(index)}
      </div>
    );
  }

  function renderSeparator(index) {
    return (
      separators &&
      index < options.length - 1 && (
        <Layout bottom="xlarge" className={getSeparatorClassName(index)}>
          <Separator {...separators} />
        </Layout>
      )
    );
  }

  function getSeparatorClassName(index) {
    const hasPrefixIcon = prefixIcon || options[index].prefixIcon;

    return classNames({
      [styles.prefixAllowance]: hasPrefixIcon,
    });
  }
};

OptionList.displayName = "OptionList";

OptionList.propTypes = {
  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Options to be displayed. */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      ...optionListItemPropTypes,
    })
  ),

  /** Prefix icon to be applied to all OptionListItems if they do not have one defined. */
  prefixIcon: optionListItemPropTypes.prefixIcon,

  /** Whether to display separators between options. */
  separators: PropTypes.shape(separatorPropTypes),

  /** Suffix icon to be applied to all OptionListItems if they do not have one defined. */
  suffixIcon: optionListItemPropTypes.suffixIcon,
};
